import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TransactionButton from 'src/common/components/Buttons/TransactionButton'
import CustomDateBadge from 'src/common/components/ExpiryBadges/CustomDateBadge'
import OfferOTCSettingsList, {
  OfferOTCSettings,
} from 'src/common/components/OfferOTCSettings/OfferOTCSettingsList'
import useUpdateOffer from 'src/common/contracts/hooks/dotc/useUpdateOffer/useUpdateOffer'
import useBreakpoint from 'src/common/hooks/ui/useBreakpoint'
import TokenDisplay from 'src/common/TokenDisplay'
import { Offer } from 'src/common/types/models/Offer'
import Button from 'src/common/ui-kit/Button/Button'
import { ZERO } from 'src/common/utils/big-utils'
import { isNFTType } from 'src/common/utils/web3/tokens'

interface EditOfferFormProps {
  offer: Offer
  onClose?: () => void
  onSuccessfulEdit?: () => void
}

const EditOfferForm: React.FC<EditOfferFormProps> = ({
  offer,
  onClose,
  onSuccessfulEdit,
}) => {
  const { t } = useTranslation('common')
  const { depositAsset: sellToken, withdrawalAsset: buyToken } = offer
  const is2xl = useBreakpoint('2xl')

  const initState = useMemo(
    () => ({
      specialAddresses: offer.specialAddresses || [],
      expiryTimestamp: offer.expiryTimestamp,
      commsLink: offer.commsLink || '',
      terms: offer.terms || '',
    }),
    [offer]
  )
  const [settings, setSettings] = useState(initState)

  const { updateOffer } = useUpdateOffer()

  const updateOfferTx = useMemo(() => {
    return updateOffer(offer.id, {
      ...offer,
      ...settings,
    })
  }, [offer, settings, updateOffer])

  useEffect(() => {
    setSettings(initState)
  }, [initState])

  const offerWithNft = isNFTType(sellToken) || isNFTType(buyToken)

  const handleChangeExpiryTimestamp = (timestamp: number) =>
    setSettings(prev => ({ ...prev, expiryTimestamp: timestamp }))

  const handleChangeSettings = (settings: OfferOTCSettings) =>
    setSettings(prev => ({
      ...prev,
      specialAddresses: settings.specialAddresses,
      commsLink: settings.commsLink,
      terms: settings.terms,
    }))

  return (
    <div className='flex flex-col gap-4'>
      <TokenDisplay
        readonly
        token={sellToken}
        amount={offer.amountIn}
        label={t('editOfferForm.sell')}
      />
      <TokenDisplay
        readonly
        token={buyToken}
        amount={offer.amountOut || ZERO}
        label={t('editOfferForm.buy')}
      />
      <div className='flex gap-2 items-center justify-between'>
        <div className='typo-label2 '>{t('editOfferForm.expire')}:</div>
        <CustomDateBadge
          onSelect={handleChangeExpiryTimestamp}
          expiryTimestamp={settings.expiryTimestamp}
          timeOptions={{
            minDate: dayjs(settings.expiryTimestamp * 1000),
          }}
          forceMobileView={is2xl}
          isActive
        />
      </div>
      <OfferOTCSettingsList
        settings={settings}
        offerWithNft={offerWithNft}
        showOfferFillType={false}
        showTimelock={false}
        showQualifier={false}
        onChangeSettings={handleChangeSettings}
      />
      <div className='flex gap-2'>
        <Button className='w-full px-2' variant='outlined' onClick={onClose}>
          {t('editOfferForm.close')}
        </Button>
        <TransactionButton
          className='w-full px-2'
          transaction={updateOfferTx}
          onSuccess={onSuccessfulEdit}
        >
          {t('editOfferForm.confirm')}
        </TransactionButton>
      </div>
    </div>
  )
}

export default EditOfferForm
